import {
  controlScoreAt,
  parsePlacement,
  squareIsAttackedBy,
  squareToIndex,
} from '@strategy4kids/game-engine';
import type { GameAdapter } from '@strategy4kids/game-engine';
import type { Fen, Turn } from '@strategy4kids/protocol';

export interface CoachFacts {
  readonly isMate: boolean;
  readonly isCheck: boolean;
  readonly isCapture: boolean;
  readonly capturedName: string | null;
  readonly defended: boolean;
  readonly develops: boolean;
  readonly controlGain: number;
  readonly movingName: string;
}

const PIECE_NAMES: Record<string, string> = {
  p: 'pawn',
  n: 'knight',
  b: 'bishop',
  r: 'rook',
  q: 'queen',
  k: 'king',
};

export function nameOf(piece: string): string {
  return PIECE_NAMES[piece.toLowerCase()] ?? 'piece';
}

export interface FactsInput {
  readonly adapter: GameAdapter;
  readonly fen: Fen;
  readonly from: string;
  readonly to: string;
  readonly turn: Turn;
}

/** Deterministic facts about a candidate move, used to build template explanations. */
export function buildFacts(input: FactsInput): CoachFacts {
  const { adapter, fen, from, to, turn } = input;
  const before = adapter.fromFen(fen);
  const after = adapter.makeMove(before, from, to);
  const moving = adapter.pieceAt(before, from);
  const movingName = moving ? nameOf(moving.char) : 'piece';
  const captured = adapter.pieceAt(before, to);
  const isCapture = captured != null;
  const capturedName = captured ? nameOf(captured.char) : null;
  const isMate = after.result?.kind === 'checkmate';
  const isCheck = adapter.isCheck(after);
  // "defended" = opponent can recapture on `to` right after our move.
  const defended = isCapture ? squareIsAttacked(after, to, turn) : false;
  const develops = Boolean(
    moving && (moving.char === 'n' || moving.char === 'b') && isBackRank(from, turn),
  );
  const controlGain = ownControl(after, turn) - ownControl(before, turn);
  return { isMate, isCheck, isCapture, capturedName, defended, develops, controlGain, movingName };
}

function squareIsAttacked(
  pos: ReturnType<GameAdapter['fromFen']>,
  sq: string,
  byOpponentOf: Turn,
): boolean {
  const attacker = byOpponentOf === 'w' ? 'b' : 'w';
  return squareIsAttackedBy(parsePlacement(pos.fen), attacker, squareToIndex(sq));
}

function ownControl(pos: ReturnType<GameAdapter['fromFen']>, turn: Turn): number {
  const placement = parsePlacement(pos.fen);
  let total = 0;
  for (let i = 0; i < 64; i++) {
    const c = controlScoreAt(placement, i);
    total += turn === 'w' ? c : -c;
  }
  return total;
}

function isBackRank(from: string, turn: Turn): boolean {
  const rank = Number(from[1]);
  return turn === 'w' ? rank === 1 : rank === 8;
}
